import { TrackEventInput } from '@updater/ui-tracker';
import {
  CardData,
  CardDataWithHeroExtras,
  InitiatePayload,
  PromoPayload,
  ROBInitiateSubtype,
} from 'flows/core/types/cards';
import { useIsMoveUPO } from 'hooks/useIsMoveUPO';
import { useEffect, useMemo } from 'react';
import { useTracking } from 'react-tracking';
import { ItemKinds } from 'types';
import { ItemStatuses } from 'constants/statuses';
import { EssentialCards } from '../types/layouts';
import {
  makeEssentialsTabForwardMailCards,
  makeEssentialsTabInsuranceCards,
  makeEssentialsTabResidentOnboardingCards,
  makeEssentialsTabTVInternetCards,
  makeEssentialsTabUtilityCards,
  makeMovingTabDraftMoveYourStuffCards,
  makeMovingTabPackingSuppliesCards,
  makeTodayTabForwardMailCards,
  makeTodayTabInsuranceCards,
  makeTodayTabMoveYourStuffCards,
  makeTodayTabPackingSuppliesCards,
  makeTodayTabResidentOnboardingCards,
  makeTodayTabTVInternetCards,
  makeTodayTabUtilityCards,
} from '../utils/card-makers';
import {
  makeEssentialsTabHomeownersInsuranceCards,
  makeTodayTabHomeownersInsuranceCards,
} from '../utils/card-makers/homeowners-insurance';
import { useDataForCards } from './use-data-for-cards';
import { useDollyEligibility } from './use-dolly-eligibility';
import { useInsuranceProviderData } from './use-insurance-provider';
import { useUserData } from './use-profile';
import { useFeature } from '@growthbook/growthbook-react';
import { Direction } from '@updater/consumer-graph';
import { useCheckFlowStatus } from '../utils/card-makers/utils';
import { CUSTOM_TASK_CARD_IDENTIFIER_PREFIX } from 'flows/resident-onboarding/constants/constants';

type TodayCardResults = {
  loading: boolean;
  error?: Error;
  cards?: CardDataWithHeroExtras[];
  heroCard?: CardDataWithHeroExtras;
};

type EssentialCardResults = {
  loading: boolean;
  error?: Error;
  toDoCards?: CardData[];
  completedCards?: CardData[];
  priorityCards?: CardData[];
  recommendedCards?: CardData[];
  validatingCards?: CardData[];
};

type MovingCardResults = {
  loading: boolean;
  error?: Error;
  cards?: CardData[];
  initiateCards?: InitiatePayload[];
  draftCards?: PromoPayload[];
};

const isMoveOutAllowedCard = (card: CardData | CardDataWithHeroExtras) =>
  card.data.subtype === 'TVI' ||
  card.identifier.startsWith(CUSTOM_TASK_CARD_IDENTIFIER_PREFIX);

export const useTodayTabCards = (): TodayCardResults => {
  const { loading, error, data } = useDataForCards();
  const { loading: dollyLoading, data: dollyData } = useDollyEligibility();
  const { data: plansData, error: plansError } = useInsuranceProviderData();
  const { loading: isMoveUPOLoading, data: { isUPO } = { isUPO: false } } =
    useIsMoveUPO();
  const { user } = useUserData();

  return useMemo(() => {
    if (!loading && error) {
      return { loading, error };
    }
    if (
      !loading &&
      !dollyLoading &&
      !isMoveUPOLoading &&
      data &&
      (plansData || plansError)
    ) {
      const isMoveOut = user?.currentMove?.direction === Direction.move_out;

      const cardArrays = [
        makeTodayTabPackingSuppliesCards(data),
        makeTodayTabForwardMailCards(data),
        makeTodayTabTVInternetCards(data),
        makeTodayTabMoveYourStuffCards(data, dollyData),
        makeTodayTabInsuranceCards(data, plansData),
        makeTodayTabUtilityCards(data, isUPO),
        makeTodayTabResidentOnboardingCards(data),
        makeTodayTabHomeownersInsuranceCards(data),
      ];
      const [heroCard, ...otherCards] = cardArrays
        .flat()
        .filter((card) => Number(card.importanceScore) > 0)
        .filter((card) => !isMoveOut || isMoveOutAllowedCard(card))
        .sort((a, b) => Number(b.importanceScore) - Number(a.importanceScore))
        .slice(0, 4);
      return {
        loading: loading || dollyLoading,
        cards: otherCards,
        heroCard,
      };
    }
    return { loading: loading || dollyLoading || !plansData };
  }, [
    loading,
    error,
    dollyLoading,
    data,
    plansData,
    plansError,
    dollyData,
    user,
  ]);
};

export const useEssentialsTabCards = (): EssentialCardResults => {
  const { loading, error, data } = useDataForCards();
  const { on: insuranceDisabled } = useFeature('exclude_renters_insurance');
  const { value: customTasksEnabled } = useFeature('consumer_app_custom_tasks');
  const { trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: 'resident_onboarding',
    pageName: 'essentials_tab',
    object: 'eligibility',
    verb: 'tracked',
  });
  const { user } = useUserData();
  const { loading: isMoveUPOLoading, data: { isUPO } = { isUPO: false } } =
    useIsMoveUPO();

  const moveId = user?.currentMove?.id;
  const { loading: loadingFlowStatus, completedFlows } = useCheckFlowStatus({
    moveId,
  });

  const essentialCards = useMemo<EssentialCardResults>(() => {
    if (!loading && error) {
      return { loading, error };
    }
    if (!loading && !isMoveUPOLoading && !loadingFlowStatus && data) {
      const cardArrays = [
        makeEssentialsTabTVInternetCards({
          ...data,
          user,
          isUPO,
        }),
        makeEssentialsTabForwardMailCards({ ...data, completedFlows }),
        insuranceDisabled
          ? ({ toDoCards: [], completedCards: [] } as EssentialCards)
          : makeEssentialsTabInsuranceCards(data),
        makeEssentialsTabUtilityCards({ ...data, isUPO, completedFlows }),
        makeEssentialsTabResidentOnboardingCards(
          data,
          isUPO,
          customTasksEnabled
        ),
        makeEssentialsTabHomeownersInsuranceCards(data),
      ];
      const isMoveOut = user?.currentMove?.direction === Direction.move_out;

      const allToDoCards = cardArrays
        .map((flow) => flow.toDoCards)
        .flat()
        .filter((card) => !isMoveOut || isMoveOutAllowedCard(card))
        .sort((a, b) => Number(b.importanceScore) - Number(a.importanceScore));
      // Filter cards based on UPO status
      const { priorityCards, recommendedCards } = isUPO
        ? {
            priorityCards: allToDoCards.filter((card) => {
              // Type guard to check if card has required/priority properties
              const cardData = card.data as {
                required?: boolean;
                priority?: boolean;
              };
              return cardData.required || cardData.priority;
            }),
            recommendedCards: allToDoCards.filter((card) => {
              // Type guard to check if card has required/priority properties
              const cardData = card.data as {
                required?: boolean;
                priority?: boolean;
              };
              return !cardData.required && !cardData.priority;
            }),
          }
        : { priorityCards: [], recommendedCards: allToDoCards };
      // Filter validating cards from priority cards
      const validatingCards = priorityCards.filter((card) => {
        const cardData = card.data as { status?: string };
        return cardData.status === ItemStatuses.VALIDATING;
      });

      return {
        loading,
        toDoCards: allToDoCards,
        priorityCards,
        recommendedCards,
        validatingCards,
        completedCards: cardArrays
          .map((flow) => flow.completedCards)
          .flat()
          .filter((card) => !isMoveOut || isMoveOutAllowedCard(card))
          .sort(
            (a, b) => Number(b.importanceScore) - Number(a.importanceScore)
          ),
      };
    }
    return { loading };
  }, [
    loading,
    error,
    isMoveUPOLoading,
    loadingFlowStatus,
    data,
    user,
    isUPO,
    completedFlows,
    insuranceDisabled,
    customTasksEnabled,
  ]);

  // track ROB cards
  // helper functions
  const hasSubIdentifier = (cardArray: CardData[], subidentifier: string) =>
    cardArray.some(({ identifier }) => identifier.includes(subidentifier));
  const utilityHasSubIdentifier = (
    cardArray: CardData[],
    utilityType:
      | ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_ELECTRIC
      | ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_GAS
      | ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_WATER
  ) =>
    cardArray.some(({ data: carrData }) =>
      (carrData as ROBInitiateSubtype).types?.includes(utilityType)
    );

  useEffect(() => {
    const {
      error: essentialCardsError,
      completedCards,
      toDoCards,
    } = essentialCards;
    if (!essentialCardsError && (completedCards || toDoCards)) {
      const allCards = [
        ...(completedCards as CardData[]),
        ...(toDoCards as CardData[]),
      ];
      const robCards = allCards.filter(
        (card: CardData) => card.data.subtype === 'ROB'
      );
      const insuranceCards = allCards.filter(
        (card: CardData) => card.data.subtype === 'SYI'
      );
      if (robCards.length > 0) {
        trackEvent({
          details: {
            pets: hasSubIdentifier(robCards, 'pets'),
            'proof-of-utilities': {
              proof_of_water: utilityHasSubIdentifier(
                robCards,
                ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_WATER
              ),
              proof_of_gas: utilityHasSubIdentifier(
                robCards,
                ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_GAS
              ),
              proof_of_electric: utilityHasSubIdentifier(
                robCards,
                ItemKinds.RESIDENT_ONBOARDING_PROOF_OF_ELECTRIC
              ),
            },
            'move-costs': hasSubIdentifier(robCards, 'move-costs'),
            'proof-of-insurance': hasSubIdentifier(robCards, 'insurance'),
            'proof-of-insurance-rob-and-assurant':
              hasSubIdentifier(robCards, 'insurance') &&
              insuranceCards.length > 0,
            vehicles: hasSubIdentifier(robCards, 'vehicle'),
          },
        });
      }
    }
  }, [
    essentialCards,
    essentialCards.toDoCards,
    essentialCards.completedCards,
    trackEvent,
  ]);

  return essentialCards;
};

export const useMovingTabCards = (): MovingCardResults => {
  const { loading, error, data } = useDataForCards();

  const movingTabData = useMemo(() => {
    if (!loading && error) {
      return { loading, error };
    }
    if (!loading && data) {
      const cardArrays = [
        makeMovingTabDraftMoveYourStuffCards(data),
        makeMovingTabPackingSuppliesCards(),
      ];
      return {
        loading,
        cards: cardArrays
          .flat()
          .sort(
            (a, b) => Number(b.importanceScore) - Number(a.importanceScore)
          ),
      };
    }
    return { loading };
  }, [loading, error, data]);

  const initiateCards = useMemo(
    () =>
      (movingTabData?.cards || []).filter(
        ({ type }) => type === 'INITIATE'
      ) as InitiatePayload[],
    [movingTabData]
  );

  const draftCards = useMemo(
    () =>
      (movingTabData?.cards || []).filter(
        ({ type }) => type === 'PROMOTED'
      ) as PromoPayload[],
    [movingTabData]
  );

  return { ...movingTabData, initiateCards, draftCards };
};
